import { useMemo, useState } from "react"
import type { WeekSchedule as WeekScheduleData } from "../lib/schedule"
import {
  getActiveDays,
  getCurrentWeekKey,
  getWeekFromMap,
  weekHasAnyRoute,
} from "../lib/schedule"
import { DaySlot } from "./DaySlot"

interface WeekScheduleProps {
  weekKeys: string[]
  weeks: Map<string, WeekScheduleData>
  initialWeekKey?: string
}

export function WeekSchedule({
  weekKeys,
  weeks,
  initialWeekKey = getCurrentWeekKey(),
}: WeekScheduleProps) {
  const initialIndex = useMemo(() => {
    const idx = weekKeys.indexOf(initialWeekKey)
    if (idx >= 0) return idx
    const fallback = weekKeys.indexOf("2026-W22")
    return fallback >= 0 ? fallback : 0
  }, [weekKeys, initialWeekKey])

  const [index, setIndex] = useState(initialIndex)
  const key = weekKeys[index] ?? weekKeys[0]
  const week = getWeekFromMap(weeks, key)
  const hasAny = weekHasAnyRoute(week)
  const activeDays = getActiveDays(week)

  const arrowButtonClass =
    "hover:text-brew-muted disabled:opacity-40 hover:bg-brew-surface-warm p-2 text-2xl font-bold font-logo"

  return (
    <div className="w-full">
      {/* Week navigation */}
      <section className="bg-brew-surface">
        <div className="flex items-center justify-between border-y border-brew-border-soft px-brew-gutter-phone py-brew-section-tablet max-[720px]:py-brew-section-phone">
          <button
            type="button"
            className={arrowButtonClass}
            aria-label="Previous week"
            disabled={index <= 0}
            onClick={() => index > 0 && setIndex(index - 1)}
          >
            ←
          </button>
          <h2 className="flex-1 px-brew-2 text-center font-logo text-xl leading-tight">
            {week.label}
          </h2>
          <button
            type="button"
            className={arrowButtonClass}
            aria-label="Next week"
            disabled={index >= weekKeys.length - 1}
            onClick={() => index < weekKeys.length - 1 && setIndex(index + 1)}
          >
            →
          </button>
        </div>
      </section>

      {/* Week schedule */}
      {!hasAny ? (
        <section className="mx-auto max-w-[40ch] px-brew-gutter-phone py-brew-12 pb-brew-section-desktop text-center">
          <h2 className="mb-brew-3 font-display text-brew-2xl uppercase tracking-brew-display">
            Nothing scheduled this week
          </h2>
        </section>
      ) : (
        <section className="px-brew-gutter-phone flex justify-center py-10">
          <div className="flex flex-col gap-brew-12 pb-brew-8 max-[720px]:gap-brew-8">
            {activeDays.map((day) => (
              <DaySlot key={day} day={day} route={week.days[day]} />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
